import { useState, useMemo, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { useUnit } from '@/modules/units/contexts/UnitContext'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { matchesSearch } from '@/lib/search'
import { useBilling } from '@/modules/billing/BillingContext'
import { Badge } from '@/shared/components/ui'
import { LOCALE_CURRENCY } from '@/shared/components/ui/CurrencyInput'
import {
  type FilterField,
  type DataTableColumn,
} from '@/shared/components/entities'
import { EntityListPage, type CardConfig } from '@/shared/components/entities/EntityListPage'
import { resolvePresetRange } from '@/lib/datePresets'
import { WhatsAppHistoryBadge } from './WhatsAppHistoryBadge'
import EntryActions from './EntryActions'
import {
  listEntries,
  createEntry,
  updateEntry,
  deleteEntry,
  fetchEntry,
  markAsPaid,
  isOverdue,
} from '@/modules/billing/entries/entriesService'
import {
  fetchNextRecurrence,
  createRecurrenceTemplate,
  updateRecurrenceTemplate,
  getRecurrenceTemplate,
  countEntriesWithTemplate,
  findPreviousTerminatedTemplate,
  reactivateTemplate,
  maybeCleanupInstallmentTemplate,
  rollTemplateOrigin,
  handoverTemplateOrigin,
  listActiveRecurrenceTemplates,
  fetchVirtualEntries,
} from '@/modules/billing/entries/recurrencesService'
import type {
  FinancialEntry,
  VirtualEntry,
  AnyEntry,
  EntryType,
  EntryStatus,
  PaymentMethod,
  RecurrencePeriodicity,
  RecurrenceTemplate,
  UpdateEntryInput,
} from '@/modules/billing/entries/entriesTypes'
import { listParties, updateParty, upsertParty } from '@/modules/registrations/contacts/contactsService'
import type { Party } from '@/modules/registrations/contacts/contactsTypes'

import EntryForm from './EntryForm'

// â”€â”€â”€ Form input â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface EntryFormInput {
  description:             string
  amount:                  string   // string in form â†’ Number on submit
  due_date:                string
  party_name_snapshot:     string
  party_id?:               string | null
  party_whatsapp_snapshot: string
  party_pix_key_snapshot:  string
  payment_method_snapshot: string   // '' | PaymentMethod
  notes:                   string
  paid_on_launch:          boolean
}

// Component: exibe periodicidade/recorrÃªncia de forma heurÃ­stica
function PeriodicityLabel({ entry }: { entry: FinancialEntry }) {
  const { t } = useTranslation()
  const [label, setLabel] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true

    // Detecta parcelamento via sufixo "N/M" na descriÃ§Ã£o (padrÃ£o usado ao replicar)
    const installMatch = String(entry.description || '').match(/^(.*?)\s+(\d+)\/(\d+)$/)
    if (installMatch) {
      const curr = installMatch[2]
      const total = installMatch[3]
      setLabel(t('billing.entries.list.periodicityValues.installment', { curr, total }))
      return
    }

    // Se hÃ¡ recurrence_template_id (nova recorrÃªncia), busca o tipo especÃ­fico
    if (entry.recurrence_template_id) {
      ;(async () => {
        try {
          const tmpl = await getRecurrenceTemplate(entry.recurrence_template_id!)
          if (!mounted) return
          if (!tmpl) {
            setLabel(t('billing.entries.list.periodicityValues.recurring'))
            return
          }
          // Map periodicity to translated label
          const periodicityMap: Record<RecurrencePeriodicity, string> = {
            daily: t('billing.entries.list.periodicityValues.daily'),
            weekly: t('billing.entries.list.periodicityValues.weekly'),
            monthly: t('billing.entries.list.periodicityValues.monthly'),
            yearly: t('billing.entries.list.periodicityValues.yearly'),
            installment: t('billing.entries.list.periodicityValues.installment', { 
              curr: 'â€”', 
              total: ((tmpl.rules_json as any)?.installment_total || 'â€”').toString() 
            }),
          }
          const periodType = (tmpl.periodicity as unknown as RecurrencePeriodicity)
          setLabel(periodicityMap[periodType] || t('billing.entries.list.periodicityValues.recurring'))
        } catch (err) {
          if (!mounted) return
          setLabel(t('billing.entries.list.periodicityValues.recurring'))
        }
      })()
      return
    }

    // Se nÃ£o hÃ¡ recurrence_template_id nem recurrence_id, Ã© registro Ãºnico
    if (!entry.recurrence_id) {
      setLabel(t('billing.entries.list.periodicityValues.oneTime'))
      return
    }

    // Caso pertenÃ§a a uma cadeia de recorrÃªncia antiga, tenta inferir pela prÃ³xima ocorrÃªncia
    ;(async () => {
      try {
        const next = await fetchNextRecurrence(entry.recurrence_id!, entry.due_date)
        if (!mounted) return
        if (!next) {
          setLabel(t('billing.entries.list.periodicityValues.recurring'))
          return
        }
        const d1 = new Date(entry.due_date)
        const d2 = new Date(next.due_date)
        const diffDays = Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24))

        if (diffDays >= 350) setLabel(t('billing.entries.list.periodicityValues.yearly'))
        else if (diffDays >= 25 && diffDays <= 45) setLabel(t('billing.entries.list.periodicityValues.monthly'))
        else if (diffDays <= 9) setLabel(t('billing.entries.list.periodicityValues.weekly'))
        else setLabel(t('billing.entries.list.periodicityValues.recurring'))
      } catch (err) {
        if (!mounted) return
        setLabel(t('billing.entries.list.periodicityValues.recurring'))
      }
    })()

    return () => { mounted = false }
  }, [entry])

  return <span className="text-sm text-slate-700 dark:text-slate-300">{label ?? 'â€”'}</span>
}

// â”€â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function formatDate(dateStr: string, locale: string) {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(locale)
}

function formatDateWithWeekday(dateStr: string, locale: string): string {
  const formatted = formatDate(dateStr, locale)
  const [y, m, d] = dateStr.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  const weekdayLong = date.toLocaleDateString(locale, { weekday: 'long' })
  // Capitalize first letter
  const weekdayCapitalized = weekdayLong.charAt(0).toUpperCase() + weekdayLong.slice(1)
  return `${formatted} (${weekdayCapitalized})`
}

type BadgeVariant = 'primary' | 'success' | 'warning' | 'danger' | 'info'

function statusBadge(entry: AnyEntry, labels: {
  open: string; overdue: string; paid: string; receivable_paid: string; payable_paid: string; canceled: string; remainder?: string; virtual?: string
}, entryType: EntryType = 'payable'): { label: string; variant: BadgeVariant } {
  if ((entry as VirtualEntry).isVirtual) return { label: labels.virtual ?? 'Previsto', variant: 'info' }
  if (isOverdue(entry)) return { label: labels.overdue,   variant: 'danger'  }
  if (entry.status === 'paid') {
    // For receivable, show 'Recebido'; for payable, show 'Pago'
    const paidLabel = entryType === 'receivable' ? labels.receivable_paid : labels.payable_paid
    return { label: paidLabel, variant: 'success' }
  }
  if (entry.status === 'canceled') return { label: labels.canceled, variant: 'info'    }
  if (entry.parent_id && labels.remainder) return { label: labels.remainder, variant: 'primary' }
  return { label: labels.open, variant: 'warning' }
}

// â”€â”€â”€ DropdownChip â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

type DropdownOption = { key: string | null; label: string; activeClass?: string }

function DropdownChip({
  value,
  options,
  onChange,
  placeholder = 'Selecionar',
  clearable = true,
}: {
  value:        string | null
  options:      DropdownOption[]
  onChange:     (v: string | null) => void
  placeholder?: string
  clearable?:   boolean
}) {
  const [open, setOpen]   = useState(false)
  const btnRef            = useRef<HTMLButtonElement>(null)
  const panelRef          = useRef<HTMLDivElement>(null)
  const [pos, setPos]     = useState({ top: 0, left: 0 })

  const selected = options.find(o => o.key === value) ?? null
  const isActive = clearable && value !== null

  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      const t = e.target as Node
      if (!btnRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      setPos({ top: r.bottom + 4, left: r.left })
    }
    setOpen(o => !o)
  }

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ${
          isActive && selected?.activeClass
            ? selected.activeClass
            : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:border-slate-400 dark:hover:border-slate-500'
        }`}
      >
        {selected?.label ?? placeholder}
        {isActive ? (
          <span
            role="button"
            aria-label="Limpar"
            onClick={(e) => { e.stopPropagation(); onChange(null); setOpen(false) }}
          >
            <svg className="h-3 w-3 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </span>
        ) : (
          <svg className="h-3 w-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          className="fixed z-[200] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1 min-w-[148px]"
          style={{ top: pos.top, left: pos.left }}
        >
          {options.map(opt => (
            <button
              key={String(opt.key)}
              onClick={() => { onChange(opt.key); setOpen(false) }}
              className={`flex items-center justify-between gap-3 w-full px-3 py-2 text-sm text-left transition-colors ${
                opt.key === value
                  ? 'text-slate-900 dark:text-white font-medium bg-slate-50 dark:bg-slate-700/50'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
              }`}
            >
              {opt.label}
              {opt.key === value && (
                <svg className="h-3.5 w-3.5 text-emerald-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  )
}

// â”€â”€â”€ Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface Props {
  entryType: EntryType
}

export function FinancialEntriesPage({ entryType }: Props) {
  const { currentUnit, userRole } = useUnit()
  const isMember = userRole === 'viewer'
  const { t, locale } = useTranslation()
  const { invalidateEntries } = useBilling()
  const location = useLocation()
  const openCreate = (location.state as any)?.openCreate === true

  const tb = (key: string, params?: Record<string, string>) => t(`billing.${key}`, params)
  const te = (key: string, params?: Record<string, string>) => t(`billing.entries.${key}`, params)
  const tf = (key: string, params?: Record<string, string>) => t(`billing.filters.${key}`, params)

  // Status filters (always visible)
  const statusFilters: DropdownOption[] = [
    { key: 'all',      label: tf('statusAll'),      activeClass: 'bg-slate-700 border-slate-700 text-white shadow-sm dark:bg-slate-200 dark:border-slate-200 dark:text-slate-900' },
    { key: 'open',     label: tf('statusOpen'),     activeClass: 'bg-amber-500 border-amber-500 text-white shadow-sm' },
    { key: 'paid',     label: tf('statusPaid'),     activeClass: 'bg-emerald-600 border-emerald-600 text-white shadow-sm' }
  ]

  // Date filters (visible only when status is All or Open)
  const dateFilters: DropdownOption[] = [
    { key: 'today',    label: tf('periodToday'),    activeClass: 'bg-blue-600 border-blue-600 text-white shadow-sm'      },
    { key: 'tomorrow', label: tf('periodTomorrow'), activeClass: 'bg-amber-500 border-amber-500 text-white shadow-sm'    },
    { key: 'week',     label: tf('periodWeek'),     activeClass: 'bg-violet-600 border-violet-600 text-white shadow-sm'  },
    { key: 'overdue',  label: tf('periodOverdue'),  activeClass: 'bg-rose-600 border-rose-600 text-white shadow-sm'      },
  ]

  const isReceivable = entryType === 'receivable'

  const currency = LOCALE_CURRENCY[locale] ?? 'BRL'

  function formatCurrency(amount: number) {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount)
  }

  const sLabels = {
    open:      tb('status.open'),
    overdue:   tb('status.overdue'),
    paid:      tb('status.paid'),
    receivable_paid: tb('status.receivable_paid'),
    payable_paid: tb('status.payable_paid'),
    canceled:  tb('status.canceled'),
    virtual:   tb('status.virtual') || 'Previsto',
    remainder: 'Saldo',
  }

  // â”€â”€ Payoff state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const todayStr = () => new Date().toISOString().split('T')[0]

  const [viewDate, setViewDate] = useState(() => new Date())

  const { startOfMonth, endOfMonth, monthLabel, isCurrentMonth } = useMemo(() => {
    const y = viewDate.getFullYear()
    const m = viewDate.getMonth()
    const mStr = String(m + 1).padStart(2, '0')
    const lastDay = new Date(y, m + 1, 0).getDate()
    const label = new Date(y, m, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' })
      .replace(/^\w/, c => c.toUpperCase())
    const now = new Date()
    return {
      startOfMonth:   `${y}-${mStr}-01`,
      endOfMonth:     `${y}-${mStr}-${String(lastDay).padStart(2, '0')}`,
      monthLabel:     label,
      isCurrentMonth: y === now.getFullYear() && m === now.getMonth(),
    }
  }, [viewDate, locale])

  const prevMonth = () => setViewDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))
  const nextMonth = () => setViewDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))
  const goToday   = () => setViewDate(new Date())

  // Separate status (all/open/paid) from period (today/tomorrow/week/overdue)
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'paid'>('all')
  const [dateFilter, setDateFilter] = useState<'overdue' | 'today' | 'tomorrow' | 'week' | null>(null)

  // Effective filter (keeps backward-compatible string used elsewhere)
  const entryFilter = (dateFilter ?? statusFilter) as 'all' | 'open' | 'overdue' | 'today' | 'tomorrow' | 'week' | 'paid'

  const showDateFilters = statusFilter === 'all' || statusFilter === 'open' || dateFilter !== null

  useEffect(() => {
    if (dateFilter === 'today') {
      goToday()
    }
  }, [dateFilter])

  // Modal state is owned by EntryActions; page keeps minimal UI state only
  const [, setConfirmSubmitOpen] = useState(false)
  // Parties (contacts) â€” used for autocomplete/lookup in the entry form
  const [parties, setParties] = useState<Party[]>([])
  // â”€â”€ Weekly state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  // â”€â”€ Recurrence configuration modal state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // Recurrence modal state moved to EntryActions

  // â”€â”€ Terminate recurrence confirmation state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // Terminate recurrence confirmation handled in EntryActions

  // â”€â”€ Receipt modal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // Receipt/Notify modals handled by EntryActions

  // â”€â”€ Virtual edit scope dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [pendingVirtualEdit, setPendingVirtualEdit] = useState<{
    templateId: string
    originalDueDate: string
    data: EntryFormInput
  } | null>(null)
  const [virtualEditSubmitting, setVirtualEditSubmitting] = useState(false)

  // If parent requested to open specific modals programmatically (headless usage)
  // initial* props for headless modal opening are now deprecated; EntryActions owns modal lifecycle.

  useEffect(() => {
    let mounted = true
    if (!currentUnit?.id) {
      setParties([])
      return
    }
    listParties(currentUnit.id)
      .then(data => { if (mounted) setParties(data) })
      .catch(err => console.error('[FinancialEntriesPage] listParties error:', err))
    return () => { mounted = false }
  }, [currentUnit?.id])

  // Recurrence preview logic moved to EntryActions

  // Headless modal lifecycle previously managed here; moved to EntryActions.

  const [reloadKey,         setReloadKey]          = useState(0)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const check = () => setIsMobile(typeof window !== 'undefined' ? window.innerWidth < 768 : false)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const refreshEntries = () => {
    setReloadKey(k => k + 1)
    invalidateEntries()
  }



  // â”€â”€ Helpers shared by virtual edit handlers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const resolveVirtualEditParty = async (data: EntryFormInput) => {
    const partyName = data.party_name_snapshot.trim()
    let partyId = data.party_id ?? undefined
    if (partyId) {
      if (partyName) {
        try { await updateParty(partyId, { name: partyName, whatsapp: data.party_whatsapp_snapshot.trim() || undefined }) }
        catch (err) { console.error('[FinancialEntriesPage] updateParty error:', err) }
      }
    } else if (partyName) {
      partyId = await upsertParty(currentUnit!.id, partyName, data.party_whatsapp_snapshot.trim() || undefined)
    }
    return partyId
  }

  const createVirtualRealEntry = async (data: EntryFormInput, partyId: string | undefined, recurrenceTemplateId: string, originalDueDate: string) => {
    return createEntry({
      unit_id:                 currentUnit!.id,
      entry_type:              entryType,
      description:             data.description.trim(),
      amount:                  Number(data.amount),
      due_date:                data.due_date,
      // Always preserve the original occurrence date so the SQL dedup can match
      // even if the user changed due_date during editing
      original_due_date:       originalDueDate,
      party_id:                partyId,
      party_name_snapshot:     data.party_name_snapshot.trim() || undefined,
      party_whatsapp_snapshot: data.party_whatsapp_snapshot.trim() || undefined,
      party_pix_key_snapshot:  data.party_pix_key_snapshot.trim() || undefined,
      payment_method_snapshot: (data.payment_method_snapshot as PaymentMethod) || undefined,
      notes:                   data.notes.trim() || undefined,
      recurrence_template_id:  recurrenceTemplateId,
    }, userRole || undefined)
  }

  // "SÃ³ esta ocorrÃªncia" â€” cria registro fÃ­sico vinculado ao template
  // (para que dedup funcione e nÃ£o duplique virtual no mesmo mÃªs)
  // Se o usuÃ¡rio depois editar essa entrada real, ela serÃ¡ desvinculada automaticamente.
  const handleVirtualEditThis = async () => {
    if (!pendingVirtualEdit || !currentUnit?.id) return
    const { templateId, originalDueDate, data } = pendingVirtualEdit
    setVirtualEditSubmitting(true)
    try {
      const partyId = await resolveVirtualEditParty(data)
      // Keep the recurrence_template_id so dedup marks the month as covered
      // and prevents duplicate virtual entries for the same month
      const newEntry = await createVirtualRealEntry(data, partyId, templateId, originalDueDate)
      // Cleanup + roll origin: template always anchored to the latest confirmed entry
      try {
        const template = await getRecurrenceTemplate(templateId)
        if (template) {
          // Cleanup first (uses pre-roll origin for counting)
          await maybeCleanupInstallmentTemplate(template)
          // Then roll origin to this newly created entry
          await rollTemplateOrigin(template, newEntry.id)
        }
      } catch (err) {
        console.warn('[FinancialEntriesPage] handleVirtualEditThis cleanup warning', err)
      }
      setPendingVirtualEdit(null)
      setReloadKey(k => k + 1)
      invalidateEntries()
    } catch (err) {
      console.error('[FinancialEntriesPage] handleVirtualEditThis error:', err)
    } finally {
      setVirtualEditSubmitting(false)
    }
  }

  // "Esta e futuras" â€” cria registro fÃ­sico + encerra template atual + cria novo template
  const handleVirtualEditFuture = async () => {
    if (!pendingVirtualEdit || !currentUnit?.id) return
    const { templateId, originalDueDate, data } = pendingVirtualEdit
    setVirtualEditSubmitting(true)
    try {
      const partyId = await resolveVirtualEditParty(data)

      // 1. Fetch the old template rules
      const oldTemplate = await getRecurrenceTemplate(templateId)
      if (!oldTemplate) throw new Error('Template nÃ£o encontrado')

      // 2. Create the real entry (linked to old template temporarily)
      const newEntry = await createVirtualRealEntry(data, partyId, templateId, originalDueDate)

      // 3. Cap the old template at the day before the edited due_date so that
      //    virtual entries before this month (e.g. May/June) are still generated.
      //    We do NOT terminate (status stays 'active') â€” the end_date cap is enough.
      const editedDate    = new Date(data.due_date + 'T12:00:00')
      editedDate.setDate(editedDate.getDate() - 1)
      const dayBeforeEdit = editedDate.toISOString().split('T')[0]
      await updateRecurrenceTemplate(templateId, { end_date: dayBeforeEdit })

      // 4. Create new template with same rules but new origin entry
      // For installment periodicity, update the installment_current_start to reflect the next parcel
      const newRules = { ...oldTemplate.rules_json }
      if (oldTemplate.periodicity === 'installment') {
        const match = newEntry.description.match(/(\d+)\/(\d+)$/)
        if (match) {
          ;(newRules as any).installment_current_start = Number(match[1])
        }
      }
      // If the due_date day changed and periodicity is monthly/installment,
      // update monthly_target_day to reflect the new day so future occurrences follow it
      if (oldTemplate.periodicity === 'monthly' || oldTemplate.periodicity === 'installment') {
        const newDay = Number(data.due_date.split('-')[2])
        newRules.monthly_target_day = newDay
      }
      const newTemplate = await createRecurrenceTemplate({
        unit_id:         currentUnit.id,
        origin_entry_id: newEntry.id,
        periodicity:     oldTemplate.periodicity as RecurrencePeriodicity,
        rules_json:      newRules,
        start_date:      data.due_date,
        origin_amount:   newEntry.amount,  // Store the original amount for future occurrences
      })

      // 5. Link the new entry to the new template
      await updateEntry(newEntry.id, { recurrence_template_id: newTemplate.id }, userRole || undefined)

      // Auto-cleanup: if this was the last installment (e.g. "esta e futuras" on the
      // final parcel), the new template has nothing more to generate â€” delete it.
      try {
        await maybeCleanupInstallmentTemplate(newTemplate)
      } catch (err) {
        console.warn('[FinancialEntriesPage] handleVirtualEditFuture cleanup warning', err)
      }

      setPendingVirtualEdit(null)
      setReloadKey(k => k + 1)
      invalidateEntries()
    } catch (err) {
      console.error('[FinancialEntriesPage] handleVirtualEditFuture error:', err)
    } finally {
      setVirtualEditSubmitting(false)
    }
  }

  // â”€â”€ Virtual entry helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const isVirtualEntry = (entry: AnyEntry): entry is VirtualEntry =>
    (entry as VirtualEntry).isVirtual === true

  // materialiseVirtualEntry moved to EntryActions

  const isPayable = (entry: AnyEntry) =>
    entry.status === 'open' || isOverdue(entry)

  // replicateOccurrences moved to EntryActions

  const PAYMENT_METHOD_OPTIONS = [
    { value: 'cash',         label: te('paymentMethods.cash')         },
    { value: 'pix',          label: te('paymentMethods.pix')          },
    { value: 'direct_debit', label: te('paymentMethods.direct_debit') },
    { value: 'dda',          label: te('paymentMethods.dda')          },
    { value: 'card',         label: te('paymentMethods.card')         },
    { value: 'deposit',      label: te('paymentMethods.deposit')      },
    ...(locale === 'pt-BR'
      ? [{ value: 'boleto', label: te('paymentMethods.boleto') }]
      : []),
  ]

  // â”€â”€ Filter fields â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const filterFields: FilterField[] = [
    {
      key:         'party_name_snapshot',
      label:       te('record.fields.partyName'),
      type:        'text',
      placeholder: te('filters.partyPlaceholder'),
    },
    {
      key:     'status',
      label:   te('record.fields.status'),
      type:    'select',
      options: [
        { value: 'open',     label: sLabels.open     },
        { value: 'paid',     label: sLabels.paid     },
        { value: 'canceled', label: sLabels.canceled },
      ],
    },
    {
      key:   'due_date',
      label: te('record.fields.dueDate'),
      type:  'daterange',
    },
  ]

  // â”€â”€ Table columns â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const tableColumns: DataTableColumn<AnyEntry>[] = [
    {
      key:   'description',
      label: te('list.columns.description'),
      render: (entry) => (
        <div className="min-w-0">
          <div className="font-medium text-slate-900 dark:text-white truncate">{entry.description}</div>
          {entry.party_name_snapshot && (
            <div className="text-sm text-slate-500 dark:text-slate-400 truncate">{entry.party_name_snapshot}</div>
          )}
          <div className="mt-1">
            <WhatsAppHistoryBadge entryId={entry.id} />
          </div>
        </div>
      ),
    },
    {
      key:   'amount',
      label: te('list.columns.amount'),
      render: (entry) => (
        <span className="font-semibold tabular-nums text-slate-900 dark:text-white">
          {formatCurrency(entry.amount)}
        </span>
      ),
    },
    {
      key:   'due_date',
      label: te('list.columns.dueDate'),
      render: (entry) => {
        const overdueClass = isOverdue(entry)
          ? 'text-rose-600 dark:text-rose-400 font-medium'
          : 'text-slate-600 dark:text-slate-400'
        return (
          <span className={`text-sm ${overdueClass}`}>
            {(entry as FinancialEntry).original_due_date && (entry as FinancialEntry).original_due_date !== entry.due_date ? (
              <span>
                <span className="line-through text-slate-400 dark:text-slate-500 mr-2">{formatDateWithWeekday((entry as FinancialEntry).original_due_date!, locale)}</span>
                <span className="font-semibold">{formatDateWithWeekday(entry.due_date, locale)}</span>
              </span>
            ) : (
              formatDateWithWeekday(entry.due_date, locale)
            )}
          </span>
        )
      },
    },
    {
      key:   'status',
      label: te('list.columns.status'),
      render: (entry) => {
        const badge = statusBadge(entry, sLabels, entryType)
        const styles: Record<BadgeVariant, string> = {
          primary: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
          success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
          warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
          danger:  'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
          info:    'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
        }
        return (
          <div className="flex flex-col items-start gap-0.5">
            <div className="flex items-center gap-1.5">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[badge.variant]}`}>
                {badge.label}
              </span>
              {(entry as any)?.notify_on_due && (
                <svg className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              )}
              {(entry as FinancialEntry).receipt_url && (
                <svg className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              )}
            </div>
            {entry.status === 'paid' && entry.paid_at && (
              <span className="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium tabular-nums">
                {formatDate(entry.paid_at.split('T')[0], locale)}
              </span>
            )}
          </div>
        )
      },
    },
    {
      key:   'periodicity',
      label: te('list.columns.periodicity') || 'RepetiÃ§Ã£o',
      render: (entry) => (
        <div className="text-sm text-slate-700 dark:text-slate-300">
          <PeriodicityLabel entry={entry} />
        </div>
      ),
    },
    // Actions column for grid (also used in cards via getExtraActions)
    {
      key: 'actions',
      label: te('list.columns.actions'),
      align: 'right',
      render: (entry) => (
        <div className="-mt-2">
          <EntryActions
            entry={entry}
            isMobile={isMobile}
            isMember={isMember}
            onActionDone={() => refreshEntries()}
          />
        </div>
      )
    },
  ]
  // Column order: description > amount > due_date > status > periodicity > (actions added last by EntityListPage)

  // â”€â”€ Card config â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const cardConfig: CardConfig<AnyEntry> = {
    getIcon: () => (
      <svg className="h-5 w-5 text-slate-700 dark:text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {isReceivable
          ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        }
      </svg>
    ),
    getTitle:    (entry) => entry.description,
    // Show party name as subtitle when available; date is shown in fields (so we can render strike-through)
    getSubtitle: (entry) => entry.party_name_snapshot
      ? entry.party_name_snapshot
      : formatDateWithWeekday(entry.due_date, locale),
    getBadge: (entry) => ({
      ...statusBadge(entry, sLabels, entryType),
      icon: (
        <>
          {(entry as any)?.notify_on_due && (
            <svg className="h-3 w-3 text-amber-600 dark:text-amber-400 mr-0.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          )}
          {(entry as FinancialEntry).receipt_url && (
            <svg className="h-3 w-3 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
          )}
        </>
      ),
    }),
    fields: [
      {
        key:      'amount',
        label:    te('list.columns.amount'),
        getValue: (entry) => formatCurrency(entry.amount),
      },
      {
        key:      'due_date',
        label:    te('list.columns.dueDate'),
        getValue: (entry) => (
          (entry as FinancialEntry).original_due_date && (entry as FinancialEntry).original_due_date !== entry.due_date ? (
            <span>
              <span className="line-through text-slate-400 dark:text-slate-500 mr-2">{formatDateWithWeekday((entry as FinancialEntry).original_due_date!, locale)}</span>
              <span className="font-semibold">{formatDateWithWeekday(entry.due_date, locale)}</span>
            </span>
          ) : (
            formatDateWithWeekday(entry.due_date, locale)
          )
        ),
      },
      {
        key:      'payment_method_snapshot',
        label:    te('record.fields.paymentMethod'),
        getValue: (entry) =>
          entry.payment_method_snapshot
            ? te(`paymentMethods.${entry.payment_method_snapshot}`)
            : 'â€”',
      },
      {
        key: 'periodicity',
        label: te('list.columns.periodicity'),
        getValue: (entry) => <PeriodicityLabel entry={entry} />,
      },
      {
        key:      'paid_at',
        label:    tb('status.paid') + ' em',
        getValue: (entry) =>
          entry.paid_at ? formatDate(entry.paid_at.split('T')[0], locale) : 'â€”',
      },
      {
        key:      'whatsapp_history',
        label:    te('list.columns.whatsappHistory'),
        getValue: (entry) => <WhatsAppHistoryBadge entryId={entry.id} />,
      },
    ],
    editLabel:   te('actions.edit'),
    deleteLabel: te('actions.delete'),
    getStamp: (entry) => {
      if (entry.status === 'paid') {
        const stampLabel = entryType === 'receivable' ? sLabels.receivable_paid : sLabels.payable_paid
        return stampLabel.toUpperCase()
      }
      return null
    },
    getExtraActions: () => [],
    getFooter: (entry) => (
      <div className="flex justify-end">
        <EntryActions entry={entry} isMobile={isMobile} isMember={isMember} onActionDone={() => refreshEntries()} />
      </div>
    ),
  }

  // â”€â”€ Entity config â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const config = {
    entityType: `financial_entries_${entryType}`,
    filterFields,
    // Do not allow selecting entries that are already paid or virtual for bulk operations
    isEntitySelectable: (entry: AnyEntry) => !isVirtualEntry(entry) && entry.status !== 'paid',

    loadData: async (): Promise<AnyEntry[]> => {
      if (!currentUnit?.id) return []
      const iso = (d: Date) => d.toISOString().split('T')[0]
      const now     = new Date()
      const today   = iso(now)
      const yest    = iso(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1))
      const tmrw    = iso(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1))
    const dow     = now.getDay()
    // Week range: Sunday (start) to Saturday (end)
    const startOfWeek = iso(new Date(now.getFullYear(), now.getMonth(), now.getDate() - dow))
    const endOfWk = iso(new Date(now.getFullYear(), now.getMonth(), now.getDate() - dow + 6))

      const from = entryFilter === 'today'    ? today
             : entryFilter === 'tomorrow' ? tmrw
             : entryFilter === 'week'     ? startOfWeek
             : startOfMonth

      const to   = entryFilter === 'overdue'  ? yest
             : entryFilter === 'today'    ? today
             : entryFilter === 'tomorrow' ? tmrw
             : entryFilter === 'week'     ? endOfWk
             : endOfMonth

      const status: EntryStatus[] | undefined = entryFilter === 'paid'                                                    ? ['paid']
                   : entryFilter === 'open' || entryFilter === 'overdue'
                     || entryFilter === 'today' || entryFilter === 'tomorrow'
                     || entryFilter === 'week'                                                 ? ['open']
                   : undefined

      const realEntries = await listEntries(currentUnit.id, {
        entry_type:  entryType,
        dueDateFrom: from,
        dueDateTo:   to,
        ...(status ? { status } : {}),
      })

      // Only show virtual entries for the full-month view (all/open filters).
      // Sub-period filters (today/tomorrow/week/overdue/paid) don't include virtuals
      // since the user is looking at concrete records.
      const isFullMonthView = entryFilter === 'all' || entryFilter === 'open'
      if (!isFullMonthView) return realEntries

      // Fetch virtual entries for the exact range needed (RPC, migration 061)
      const allVirtuals = await fetchVirtualEntries(currentUnit.id, from, to)

      // Filter by entry_type (date range already applied by the RPC)
      const filteredVirtuals = allVirtuals.filter(v => v.entry_type === entryType)

      // Merge and sort by due_date
      return [...realEntries, ...filteredVirtuals]
        .sort((a, b) => a.due_date.localeCompare(b.due_date))
    },

    createEntity: async (data: EntryFormInput): Promise<FinancialEntry> => {
      if (!currentUnit?.id) throw new Error('No unit selected')

      // Register or resolve the party when a name or selection is provided
      const partyName = data.party_name_snapshot.trim()
      let partyId = data.party_id ?? undefined

      // If user selected an existing party, prefer that id and update whatsapp/name if provided
      if (partyId) {
        if (partyName) {
          try {
            await updateParty(partyId, { name: partyName, whatsapp: data.party_whatsapp_snapshot.trim() || undefined })
          } catch (err) {
            console.error('[FinancialEntriesPage] updateParty error:', err)
          }
        }
      } else if (partyName) {
        // Fallback: upsert by name (keeps existing behavior when not selecting)
        partyId = await upsertParty(currentUnit.id, partyName, data.party_whatsapp_snapshot.trim() || undefined)
      }

      const entry = await createEntry({
        unit_id:                 currentUnit.id,
        entry_type:              entryType,
        description:             data.description.trim(),
        amount:                  Number(data.amount),
        due_date:                data.due_date,
        party_id:                partyId,
        party_name_snapshot:     partyName     || undefined,
        party_whatsapp_snapshot: data.party_whatsapp_snapshot.trim() || undefined,
        party_pix_key_snapshot:  data.party_pix_key_snapshot.trim() || undefined,
        payment_method_snapshot: (data.payment_method_snapshot as PaymentMethod) || undefined,
        notes:                   data.notes.trim()                   || undefined,
      }, userRole || undefined)

      // If user requested the entry to be considered paid on creation,
      // compute payment date as min(due_date, today) and mark it as paid.
      if (data.paid_on_launch) {
        const todayIso = new Date().toISOString().split('T')[0]
        const paidDate = data.due_date <= todayIso ? data.due_date : todayIso
        await markAsPaid(entry.id, paidDate, userRole || undefined)
        const updated = await fetchEntry(entry.id)
        invalidateEntries()
        return (updated as FinancialEntry)
      }

      invalidateEntries()
      return entry
    },

    updateEntity: async (id: string, data: EntryFormInput): Promise<FinancialEntry> => {
      if (!currentUnit?.id) throw new Error('No unit selected')

      // If this is a virtual entry ID, show scope dialog instead of saving directly
      // Format: "virtual-<UUID(36)>-<YYYY-MM-DD>" â€” UUID always 36 chars, positions 8..44
      if (id.startsWith('virtual-')) {
        const templateId = id.substring(8, 44)
        // Extract original occurrence date from virtual id: "virtual-<36-uuid>-<YYYY-MM-DD>"
        const originalDueDate = id.substring(45)
        setPendingVirtualEdit({ templateId, originalDueDate, data })
        // Throw empty error: modal stays open, no error shown (EntityListPage shows empty string)
        throw new Error('')
      }

      // Fetch the entry before updating to check if it has recurrence_template_id
      const existingEntry = await fetchEntry(id)
      if (!existingEntry) throw new Error('Entry not found')

      // Register or resolve the party when a name or selection is provided
      const partyName = data.party_name_snapshot.trim()
      let partyId = data.party_id ?? undefined

      if (partyId) {
        if (partyName) {
          try {
            await updateParty(partyId, { name: partyName, whatsapp: data.party_whatsapp_snapshot.trim() || undefined })
          } catch (err) {
            console.error('[FinancialEntriesPage] updateParty error:', err)
          }
        }
      } else if (partyName) {
        partyId = await upsertParty(currentUnit.id, partyName, data.party_whatsapp_snapshot.trim() || undefined)
      }

      // Build the update payload and keep a copy without due_date for recurrence propagation
      const propagatableInput: Omit<UpdateEntryInput, 'due_date'> = {
        description:             data.description.trim(),
        amount:                  Number(data.amount),
        party_id:                partyId,
        party_name_snapshot:     partyName     || undefined,
        party_whatsapp_snapshot: data.party_whatsapp_snapshot.trim() || undefined,
        party_pix_key_snapshot:  data.party_pix_key_snapshot.trim() || undefined,
        payment_method_snapshot: (data.payment_method_snapshot as PaymentMethod) || undefined,
        notes:                   data.notes.trim()                   || undefined,
      }

      const entry = await updateEntry(id, { ...propagatableInput, due_date: data.due_date }, userRole || undefined)
      invalidateEntries()

      // If user requested the entry to be marked as paid on update,
      // compute payment date as min(due_date, today) and mark it as paid.
      if (data.paid_on_launch && entry.status !== 'paid') {
        const todayIso = new Date().toISOString().split('T')[0]
        const paidDate = data.due_date <= todayIso ? data.due_date : todayIso
        await markAsPaid(entry.id, paidDate, userRole || undefined)
        const updated = await fetchEntry(entry.id)
        invalidateEntries()
        return (updated as FinancialEntry)
      }

      return entry
    },

    deleteEntity: async (id: string): Promise<void> => {
      if (id.startsWith('virtual-')) return  // Virtual entries cannot be deleted
      // Fetch the entry before deleting so we can check recurrence_id and recurrence_template_id
      const entryBefore = await fetchEntry(id)

      // IMPORTANT: origin_entry_id has ON DELETE CASCADE, so deleting the entry will
      // automatically cascade-delete its template. All recovery checks must happen BEFORE
      // the entry is deleted.
      let previousTemplateToReactivate: RecurrenceTemplate | null = null
      if (entryBefore?.recurrence_template_id && currentUnit?.id) {
        try {
          const template = await getRecurrenceTemplate(entryBefore.recurrence_template_id)
          if (template) {
            // If deleting the current origin, pass the baton to the next most
            // recent linked entry so the template is not cascade-deleted
            if (entryBefore.id === template.origin_entry_id) {
              await handoverTemplateOrigin(template.id, entryBefore.id)
            }
            // Count other confirmed entries linked to this template (excluding the origin)
            const otherCount = await countEntriesWithTemplate(template.id, template.origin_entry_id)
            // If no other entries: this is the only confirmation â€” safe to undo the split
            if (otherCount === 0) {
              previousTemplateToReactivate = await findPreviousTerminatedTemplate(
                currentUnit.id,
                template.start_date,
              )
            }
          }
        } catch (err) {
          console.warn('[FinancialEntriesPage] template recovery pre-fetch warning:', err)
        }
      }

      await deleteEntry(id, userRole || undefined)
      // Template B was cascade-deleted by PostgreSQL along with the entry.
      // Now just reactivate the previous template A (clears its end_date).
      if (previousTemplateToReactivate) {
        try {
          await reactivateTemplate(previousTemplateToReactivate.id)
        } catch (err) {
          console.warn('[FinancialEntriesPage] template recovery warning:', err)
        }
      }

      invalidateEntries()

    },

    applyFilters: (entries: AnyEntry[], searchQuery: string, filterValues: Record<string, any>) => {
      return entries.filter((entry) => {
        // Quick search across description + party name (accent- and case-insensitive)
          if (searchQuery.trim()) {
            const q = searchQuery.trim()
            const inDescription = matchesSearch(entry.description, q)
            const inParty = matchesSearch(entry.party_name_snapshot ?? '', q)

            // Numeric matching (exact, partial, range, approximate and comparisons)
            let inAmount = false

            // Helper to parse numeric token like "1.234,56" or "1234.56"
            const parseNum = (s: string) => {
              if (!s) return NaN
              const cleaned = s.replace(/[^0-9.,-]/g, '').replace(/\./g, '').replace(',', '.')
              return parseFloat(cleaned)
            }

            // Range: "min-max"
            const rangeMatch = q.match(/^(\d[0-9.,]*)\s*-\s*(\d[0-9.,]*)$/)
            if (rangeMatch) {
              const min = parseNum(rangeMatch[1])
              const max = parseNum(rangeMatch[2])
              if (!isNaN(min) && !isNaN(max)) {
                inAmount = entry.amount >= Math.min(min, max) && entry.amount <= Math.max(min, max)
              }
            }

            // Approx: "~100" meaning within +/-5% (or at least 1 unit)
            const approxMatch = q.match(/^~\s*(\d[0-9.,]*)$/)
            if (!inAmount && approxMatch) {
              const v = parseNum(approxMatch[1])
              if (!isNaN(v)) {
                const tol = Math.max(1, Math.abs(v) * 0.05)
                inAmount = Math.abs(entry.amount - v) <= tol
              }
            }

            // Comparison operators: "> 100", "<=200"
            const compMatch = q.match(/^([<>]=?)\s*(\d[0-9.,]*)$/)
            if (!inAmount && compMatch) {
              const op = compMatch[1]
              const v = parseNum(compMatch[2])
              if (!isNaN(v)) {
                if (op === '>') inAmount = entry.amount > v
                else if (op === '>=') inAmount = entry.amount >= v
                else if (op === '<') inAmount = entry.amount < v
                else if (op === '<=') inAmount = entry.amount <= v
              }
            }

            // Plain numeric token: try exact, partial or proximity match
            if (!inAmount && /[0-9]/.test(q)) {
              const parsed = parseNum(q)
              if (!isNaN(parsed)) {
                const tol = Math.max(0.01, Math.abs(parsed) * 0.05)
                // Match only exact or within tolerance to avoid substring hits (e.g. 500 matching 5000)
                inAmount = entry.amount === parsed || Math.abs(entry.amount - parsed) <= tol
              }
            }

            if (!inDescription && !inParty && !inAmount) return false
          }

        // Advanced: party name
        if (filterValues.party_name_snapshot?.trim()) {
          if (!matchesSearch(entry.party_name_snapshot ?? '', filterValues.party_name_snapshot)) return false
        }

        // Advanced: status (open/paid/canceled)
        if (filterValues.status) {
          if (entry.status !== filterValues.status) return false
        }

        // Advanced: due_date range
        const dv = filterValues.due_date
        if (dv && (dv.start || dv.end || dv.preset)) {
          let start: string | null = dv.start || null
          let end:   string | null = dv.end   || null
          if (dv.preset) {
            const resolved = resolvePresetRange(dv.preset)
            start = resolved.start
            end   = resolved.end
          }
          if (start && entry.due_date < start) return false
          if (end   && entry.due_date > end)   return false
        }

        return true
      })
    },

    validateForm: (data: EntryFormInput) => {
      const errors: Record<string, string> = {}
      if (!data.description.trim())    errors.description = te('messages.descriptionRequired')
      if (!data.amount || Number(data.amount) <= 0) errors.amount = te('messages.amountPositive')
      if (!data.due_date)              errors.due_date    = te('messages.dueDateRequired')
      return errors
    },
  }

  // â”€â”€ Labels â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const labels = {
    title:             isReceivable ? tb('navigation.receivable')       : tb('navigation.payable'),
    description:       isReceivable ? te('list.descriptionReceivable')  : te('list.descriptionPayable'),
    newButton:         isReceivable ? tb('actions.newReceivable')        : tb('actions.newPayable'),
    searchPlaceholder: isReceivable ? te('filters.searchReceivable')    : te('filters.searchPayable'),
    emptyTitle:        isReceivable ? te('list.empty.receivable.title') : te('list.empty.payable.title'),
    emptyDescription:  isReceivable ? te('list.empty.receivable.description') : te('list.empty.payable.description'),
    modalTitleNew:     isReceivable ? te('record.new.receivable')       : te('record.new.payable'),
    modalTitleEdit:    te('record.edit'),
    modalTitleView:    te('record.view'),
    deleteTitle:       te('record.delete'),
    deleteMessage:     te('record.deleteConfirm'),
    deleteButton:      te('actions.delete'),
    cancelButton:      te('actions.cancel'),
    submitButton:      te('actions.create'),
    updateButton:      te('actions.update'),
    viewCard:          te('filters.viewCard'),
    viewTable:         te('filters.viewTable'),
    clearSearch:       te('filters.clear'),
  }

  // â”€â”€ Icon â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const icon = isReceivable ? (
    <svg className="h-7 w-7 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ) : (
    <svg className="h-7 w-7 text-rose-600 dark:text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  )

  // â”€â”€ Form renderer â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const renderForm = (
    formData: EntryFormInput,
    setFormData: (data: EntryFormInput) => void,
    formErrors: Record<string, string>,
    submitting: boolean,
    _editingEntity: AnyEntry | null,
    mode: 'create' | 'edit' | 'view'
  ) => {
    return (
      <EntryForm
        formData={formData}
        setFormData={setFormData}
        formErrors={formErrors}
        submitting={submitting}
        editingEntity={_editingEntity ?? null}
        mode={mode}
        parties={parties}
        paymentMethodOptions={PAYMENT_METHOD_OPTIONS}
        setConfirmSubmitOpen={setConfirmSubmitOpen}
      />
    )
  }

  // â”€â”€ Render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const chipsLeading = (
    <>
      {/* Month navigator */}
      <button onClick={prevMonth} className="p-1 rounded text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors shrink-0">
        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <span className="text-xs font-semibold text-slate-900 dark:text-white whitespace-nowrap shrink-0">{monthLabel}</span>
      <button onClick={nextMonth} className="p-1 rounded text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors shrink-0">
        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      {!isCurrentMonth && (
        <button onClick={goToday} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors shrink-0 whitespace-nowrap">
          {tb('dashboard.calendar.today')}
        </button>
      )}

      <span className="w-px h-4 bg-slate-200 dark:bg-slate-600 shrink-0" />

      {/* Status filter badges */}
      {statusFilters.map(opt => (
        <button
          key={opt.key}
          onClick={() => { setStatusFilter(opt.key as 'all' | 'open' | 'paid'); if (opt.key === 'paid') setDateFilter(null) }}
          className={`shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ${
            statusFilter === opt.key
              ? opt.activeClass
              : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-slate-400 dark:hover:border-slate-500'
          }`}
        >
          {opt.label}
        </button>
      ))}

      {/* Date filter dropdown (visible only when status is All or Open) */}
      {showDateFilters && (
        <>
          <span className="w-px h-4 bg-slate-200 dark:bg-slate-600 shrink-0" />
          <DropdownChip
            value={dateFilter as any}
            options={dateFilters}
            onChange={(v) => setDateFilter(v as 'overdue' | 'today' | 'tomorrow' | 'week' | null)}
            placeholder={tf('periodFilter')}
            clearable={true}
          />
        </>
      )}
    </>
  )

  return (
    <>
      <EntityListPage<AnyEntry, EntryFormInput>
        key={`${entryType}-${currentUnit?.id}-${startOfMonth}-${entryFilter}-${reloadKey}`}
        config={config}
        labels={labels}
        icon={icon}
        filterFields={filterFields}
        tableColumns={tableColumns}
        userRole={userRole}
        namespace={`billing.entries`}
        actionsColumnLabel={te('list.columns.actions')}
        cardConfig={cardConfig}
        chipsLeading={chipsLeading}
        renderSummary={(entries) => {
          const total = entries
            .filter(e => e.status !== 'canceled')
            .reduce((s, e) => s + e.amount, 0)
          return (
            <Badge variant={isReceivable ? 'success' : 'danger'}>
              {formatCurrency(total)}
            </Badge>
          )
        }}
        initialOpenCreate={openCreate}
        renderForm={renderForm}
        canEdit={(entry) => entry.status !== 'paid'}
        canDelete={(entry) => !isVirtualEntry(entry) && entry.status !== 'paid'}
        extraRowActions={() => []}
        extraViewActions={() => []}
        entityToFormData={(entry) => ({
          description:             entry.description,
          amount:                  String(entry.amount),
          due_date:                entry.due_date,
          party_name_snapshot:     entry.party_name_snapshot     ?? '',
          party_whatsapp_snapshot: entry.party_whatsapp_snapshot ?? '',
          party_pix_key_snapshot:  entry.party_pix_key_snapshot  ?? '',
          payment_method_snapshot: entry.payment_method_snapshot ?? '',
          notes:                   entry.notes                   ?? '',
          paid_on_launch:          false,
        })}
        emptyFormData={() => ({
          description:             '',
          amount:                  '',
          due_date:                new Date().toISOString().split('T')[0],
          party_name_snapshot:     '',
          party_whatsapp_snapshot: '',
          party_pix_key_snapshot:  '',
          payment_method_snapshot: '',
          notes:                   '',
          paid_on_launch:          false,
        })}
      />

      {/* Modais agora sÃ£o gerenciados por `EntryActions` (componentes duplicados removidos) */}
    </>
  )
}
